import { useState } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContattiPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts`, formData);
      setIsSubmitted(true);
      toast.success('Messaggio inviato con successo!');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio. Riprova.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contatti-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="hero-dark py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="accent-line mb-8"></div>
            <h1 data-testid="contatti-headline" className="text-4xl md:text-6xl font-extrabold text-white mb-6">
              Contattaci
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl">
              Hai bisogno di risorse o vuoi entrare nel nostro network? 
              Compila il form e ti risponderemo entro 24 ore.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 md:py-32 bg-[#F5F5F7] noise-bg relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div>
                <h2 className="text-2xl font-bold text-[#1A1A2E] mb-8">Inviaci un messaggio</h2>
                
                {isSubmitted ? (
                  <div data-testid="contact-success" className="bg-[#27AE60]/10 border-2 border-[#27AE60] p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-[#27AE60] mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-[#1A1A2E] mb-2">Messaggio inviato!</h3>
                    <p className="text-[#555555]">Ti risponderemo al più presto.</p>
                    <button 
                      onClick={() => setIsSubmitted(false)}
                      className="btn-secondary mt-6"
                    >
                      Invia un altro messaggio
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="font-bold text-[#1A1A2E] mb-2 block">Nome *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="contact-name-input"
                          className="input-industrial w-full"
                          placeholder="Il tuo nome"
                        />
                      </div>
                      <div>
                        <label className="font-bold text-[#1A1A2E] mb-2 block">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="contact-email-input"
                          className="input-industrial w-full"
                          placeholder="La tua email"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="font-bold text-[#1A1A2E] mb-2 block">Telefono</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          data-testid="contact-phone-input"
                          className="input-industrial w-full"
                          placeholder="Il tuo numero"
                        />
                      </div>
                      <div>
                        <label className="font-bold text-[#1A1A2E] mb-2 block">Azienda</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          data-testid="contact-company-input"
                          className="input-industrial w-full"
                          placeholder="Nome azienda"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="font-bold text-[#1A1A2E] mb-2 block">Messaggio *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        data-testid="contact-message-input"
                        className="input-industrial w-full resize-none"
                        placeholder="Come possiamo aiutarti?"
                      />
                    </div>
                    <div className="flex items-start gap-3">
                      <input 
                        type="checkbox" 
                        required 
                        id="privacy"
                        data-testid="contact-privacy-checkbox"
                        className="mt-1"
                      />
                      <label htmlFor="privacy" className="text-sm text-[#555555]">
                        Accetto la <a href="#" className="text-[#E94560] underline">Privacy Policy</a> e il trattamento dei miei dati personali.
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-button"
                      className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <><Loader2 className="animate-spin" size={20} /> Invio in corso...</>
                      ) : (
                        <><Send size={20} /> Invia messaggio</>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Info & Map */}
              <div>
                <h2 className="text-2xl font-bold text-[#1A1A2E] mb-8">Informazioni di contatto</h2>
                
                <div className="space-y-6 mb-10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1A1A2E] flex items-center justify-center">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-[#1A1A2E]">Email</p>
                      <a href="mailto:commerciale@sellandmore.it" className="text-[#555555] hover:text-[#E94560]">commerciale@sellandmore.it</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1A1A2E] flex items-center justify-center">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-[#1A1A2E]">Telefono</p>
                      <a href="tel:+393664158246" className="text-[#555555] hover:text-[#E94560]">+39 366 415 8246</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1A1A2E] flex items-center justify-center">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-[#1A1A2E]">Sede</p>
                      <p className="text-[#555555]">Rovereta - Repubblica di San Marino</p>
                    </div>
                  </div>
                </div>

                {/* Google Maps Embed - San Marino */}
                <div className="bg-white border-2 border-gray-200 overflow-hidden">
                  <iframe
                    data-testid="contact-map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11534.285799314997!2d12.4459!3d43.9422!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132d4e1c4b4b0001%3A0x97f9e7c4e0f5b4a9!2sRovereta%2C%20San%20Marino!5e0!3m2!1sit!2sit!4v1704067200000!5m2!1sit!2sit"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mappa sede SELL&REC - San Marino"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
